import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Fragment } from "preact"
import { useEffect, useMemo, useRef, useState } from "preact/hooks"
import { Link, useLocation, useRoute } from "wouter-preact"
import {
  AccountingInvoiceJson,
  ApplyCashApplicationRequest,
  CashApplicationPreview,
  CheckDetail,
  CreditMemoPreviewLine,
  AlreadyCreditedLine,
  PriceTrueUp,
  ClosedInvoiceWash,
  InvoiceAssignmentInput,
  InvoiceAssignmentLine,
  LedgerAccountJson,
  QbCustomer,
  TransactionJson,
  UncodedTransaction,
  applyCashApplication,
  listAccountingInvoices,
  listLedgerAccounts,
  listQbCustomers,
  prepareCashApplication,
} from "@/api/accounting.tsx"
import { api_fetch } from "@/api/client.tsx"
import { ArrowLeft, ArrowLeftRight, CheckCircle, ChevronDown, ChevronRight, CreditCard, DollarSign, ExternalLink, FileText, Layers, List, Loader2, Lock, Receipt } from "lucide-react"
import { format, parseISO } from "date-fns"
import { exactCurrencyFormatter, toISODateString } from "@/utils"
import { AutocompleteSelect, SelectItem } from "@/accounting/autocomplete_select.tsx"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/use-toast"
import { AccountingInvoicePicker } from "@/accounting/AccountingInvoicePicker.tsx"
import { DepositAccountField } from "@/accounting/DepositAccountField.tsx"
import { CustomerGroupsCard } from "@/accounting/CustomerGroupsCard.tsx"
import {
  all_groups_assigned,
  build_group_selections,
  choose_group_customers_hint,
  chosen_customer_ids,
  seed_group_customers,
} from "@/accounting/customer_groups"
import { OpenDeductionsAccountControl } from "@/accounting/OpenDeductionsAccountControl.tsx"
import { AccountingSyncExplainer, ApplyExplainer } from "@/accounting/accounting_explainers"
import { capture_event } from "@/utils/analytics"
import { XeroReconnectNotice, XERO_ATTACHMENTS_RECONNECT_MESSAGE, isXeroReauthError } from "@/accounting/xero_reconnect"
import { PostingDateCell } from "@/accounting/PostingDateCell"
import {
  build_posting_date_overrides,
  first_postable_date,
  locked_execution_dates,
  resolve_posting_date,
} from "@/accounting/xero_period_lock"
import { credit_memo_line_product_service, is_item_based_accounting_system } from "@/accounting/product_service_mapping"
import { Checkbox } from "@/components/ui/checkbox"
import {
  advance_behind_line,
  build_rebill_settlement_note,
  rebill_settlement_behind_line,
  amount_to_cents,
  compute_apply_balance,
  dedupe_invoices_by_id,
  build_overpaid_invoices_error,
  build_payment_balance_error,
  build_prepayment_summary,
  build_repayment_invoice_note,
  build_repayment_invoice_steps,
  apply_blockers,
  build_uncoded_transactions_error,
  build_missing_product_group_note,
  build_missing_chargeback_type_note,
  build_unapplied_cash_note,
  can_leave_line_unapplied,
  build_unresolved_prepayments_error,
  line_settle_cents_by_id,
  plan_invoice_reservations,
  collect_splits_for_gl_line,
  build_overcoded_transactions_error,
  build_invoice_lookup,
  build_unassigned_invoices_error,
  describe_unassigned_invoice_line,
  build_partial_payment_note,
  build_over_settlement_note,
  build_bi_allowance_lines,
  build_already_credited_note,
  build_bi_allowance_note,
  plan_bi_allowance_rows,
  additional_discount_rows,
  payment_discount_reduction_rows,
  remittance_early_pay_discount,
  build_discount_reduction_lines,
  short_pay_offer_rows,
  can_book_short_pay_deduction,
  build_additional_discount_lines,
  compute_settlement_breakdown,
  PrepaymentSummaryRow,
  summarize_invoice_links,
  credit_memo_split_amount,
  find_auto_assigned_qb_customer,
  find_overpaid_invoices,
  format_split_gl_summary,
  seed_auto_assignments_for_apply_page,
  sort_qb_customers_by_recommendation,
} from "@/accounting/cash_application_display.ts"
import { check_api_path, check_detail_path, decode_check_number } from "./check_path"
import { InvoiceBreakdownCard } from "@/accounting/InvoiceBreakdownCard.tsx"
import { breakdown_is_stale, picks_from_assignments } from "@/accounting/invoice_breakdown"

function formatDate(dateStr?: string): string {
  if (!dateStr) {
    return "-"
  }
  try {
    return format(parseISO(dateStr), "MM/dd/yyyy")
  } catch {
    return dateStr
  }
}

interface OtherTransactionSplitsCellProps {
  transaction: TransactionJson | undefined
}

function OtherTransactionSplitsCell({ transaction }: OtherTransactionSplitsCellProps) {
  if (!transaction || transaction.splits.length === 0) {
    return <span class="text-sm text-slate-500">No splits</span>
  }
  return (
    <ul class="space-y-2 max-w-xl">
      {transaction.splits.map((s) => (
        <li key={s.id} class="text-sm leading-snug border-b border-slate-100 last:border-0 pb-2 last:pb-0">
          <div class="font-medium text-slate-900 tabular-nums">{format_split_gl_summary(s)}</div>
          <div class="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 mt-0.5">
            {/* Show the signed credit-memo contribution so reversals/repayments read as negative. */}
            <span class="tabular-nums text-slate-700">
              {exactCurrencyFormatter(credit_memo_split_amount(transaction.invoice_amount, s.amount))}
            </span>
            {s.code_description ? (
              <span class="text-xs text-slate-500 break-words">{s.code_description}</span>
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  )
}

/**
 * Opens a deduction's accounting detail page in a new tab so the user can re-code it without losing
 * the apply page. Used to jump to individual lines that roll into a credit memo line / GL account.
 */
/**
 * A credit memo line's value for a list field the org's lines carry (NetSuite "Related Product
 * Group", "Chargeback Type"), or "none" in amber. Renders nothing when the org has no such field.
 */
function LineListFieldValue({ field_name, value }: { field_name?: string | null; value?: string | null }) {
  if (!field_name) {
    return null
  }
  return (
    <div class="text-xs mt-0.5">
      <span class="text-slate-400">{field_name}: </span>
      {value ? <span class="text-slate-600 font-medium">{value}</span> : <span class="text-amber-600">none</span>}
    </div>
  )
}

function AccountingDetailLink({ deductionId, label }: { deductionId: string; label?: string }) {
  return (
    <a
      href={`/accounting/transaction/${deductionId}`}
      target="_blank"
      rel="noreferrer"
      title="Open to view or re-code"
      class="inline-flex items-center gap-1 text-slate-400 hover:text-blue-600 hover:underline">
      {label ? <span class="text-blue-600">{label}</span> : null}
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  )
}

export function CashApplicationConfirmPage() {
  const [, params] = useRoute("/accounting/check/:checkNumber/apply")
  const [, setLocation] = useLocation()
  const checkNumber = params?.checkNumber ? decode_check_number(params.checkNumber) : undefined

  const urlParams = new URLSearchParams(window.location.search)
  const accountingCheckId = urlParams.get("accountingCheckId")

  const [checkDetail, setCheckDetail] = useState<CheckDetail | null>(null)
  const [preview, setPreview] = useState<CashApplicationPreview | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isConfirming, setIsConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [applyError, setApplyError] = useState<string | null>(null)
  const [acceptShortPay, setAcceptShortPay] = useState(false)
  // Short pay → open deduction: book each short as a deduction on the check (it parks in the
  // open-deductions account and closes the invoice) rather than leaving it due on the invoice.
  // Only offered where `can_book_short_pay_deduction` allows; on by default there. Where it is offered the
  // two outcomes are one either/or choice, and picking one is the confirmation (`acceptShortPay`).
  const [shortPayAsDeduction, setShortPayAsDeduction] = useState(true)
  // Additional discounts (non-Xero): book each invoice's gap as a credit memo line to this account.
  // `null` until the user picks one; the org's early-pay account is the default when it has one.
  const [additionalDiscountAccountId, setAdditionalDiscountAccountId] = useState<string | null>(null)
  const [acceptAdditionalDiscount, setAcceptAdditionalDiscount] = useState(false)
  // QuickBooks Product/Service items missing for the coded GL accounts — offers a one-click
  // "create them in QuickBooks" retry when the publish fails on unmapped accounts.
  const [missingItems, setMissingItems] = useState<string[]>([])
  const [accountingInvoices, setAccountingInvoices] = useState<AccountingInvoiceJson[]>([])
  const [invoicesLoading, setInvoicesLoading] = useState(false)
  // Which customer the loaded invoices are scoped to: `undefined` = not loaded yet, `null` = loaded
  // unscoped (all invoices), string = loaded for that customer remote id. Guards the re-fetch effect
  // from redundantly reloading the customer loadData already fetched for.
  const invoicesLoadedForCustomer = useRef<string | null | undefined>(undefined)
  const [assignments, setAssignments] = useState<Record<string, string>>({})
  // Invoice lines whose cash stays unapplied on the customer's account instead of being matched to
  // an invoice (a line paying an invoice the accounting system already shows as paid in full).
  const [unappliedIds, setUnappliedIds] = useState<Set<string>>(() => new Set())
  const [expanded_cm_keys, set_expanded_cm_keys] = useState<Set<string>>(() => new Set())
  // Payment details (create-payment flow)
  const [customers, setCustomers] = useState<QbCustomer[]>([])
  const [ledgerAccounts, setLedgerAccounts] = useState<LedgerAccountJson[]>([])
  const [customerId, setCustomerId] = useState<string | null>(null)
  // A check spanning several Ahold vendor accounts (NetSuite): the customer chosen per group.
  const [groupCustomers, setGroupCustomers] = useState<Record<string, string>>({})
  const customer_groups = preview?.customer_groups ?? []
  const split_mode = customer_groups.length > 0
  // What the invoice picker is scoped to: the one customer, or every customer chosen for a group.
  const invoice_scope = split_mode ? chosen_customer_ids(customer_groups, groupCustomers).join(",") : customerId
  const [depositAccountId, setDepositAccountId] = useState<string | null>(null)
  const [paymentDate, setPaymentDate] = useState<string>("")
  // Xero period locks: the user's chosen posting date per locked execution date. Empty means every
  // locked line takes the default (the payment date). Keyed by execution date because moving a
  // closed period is one decision for every line in it.
  const [postingDates, setPostingDates] = useState<Record<string, string>>({})
  // The per-invoice breakdown is the server's plan for the picks it was given. When the page's
  // picks move on, fetch it again for them (nothing is written) rather than recompute it here.
  const [breakdownRefreshing, setBreakdownRefreshing] = useState(false)

  useEffect(() => {
    if (checkNumber) {
      void loadData()
    }
  }, [checkNumber, accountingCheckId])

  useEffect(() => {
    if (!preview || isLoading || invoicesLoading || !breakdown_is_stale(preview.invoice_breakdown, assignments)) {
      return
    }
    let cancelled = false
    const picks = picks_from_assignments(assignments)
    const timer = setTimeout(async () => {
      setBreakdownRefreshing(true)
      const result = await prepareCashApplication(preview.check_id, accountingCheckId ?? undefined, picks)
      if (cancelled) {
        return
      }
      setBreakdownRefreshing(false)
      if (result.ok) {
        const fresh = result.value.data.invoice_breakdown
        setPreview((prev: CashApplicationPreview | null) => (prev ? { ...prev, invoice_breakdown: fresh } : prev))
      }
    }, 400)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [preview, assignments, isLoading, invoicesLoading, accountingCheckId])

  // Re-scope the invoice list when the user changes the customer. The initial customer resolution is
  // already fetched by loadData (tracked via the ref), so this only fires for user-driven changes.
  useEffect(() => {
    if (!preview) {
      return
    }
    if (invoicesLoadedForCustomer.current === undefined || invoicesLoadedForCustomer.current === invoice_scope) {
      return
    }
    let cancelled = false
    async function reload() {
      setInvoicesLoading(true)
      await load_invoices_and_seed(invoice_scope, preview!, () => !cancelled)
      if (!cancelled) {
        setInvoicesLoading(false)
      }
    }
    void reload()
    return () => {
      cancelled = true
    }
  }, [invoice_scope, preview])

  /**
   * Fetch the accounting invoices scoped to `customerRemoteId` (server falls back to all invoices when
   * that customer has none), store them, and (re)seed auto-assignments from the preview + fuzzy match.
   * `shouldApply` guards against a superseded fetch (rapid customer switching) writing stale results.
   */
  async function load_invoices_and_seed(
    customerRemoteId: string | null,
    p: CashApplicationPreview,
    shouldApply: () => boolean = () => true
  ) {
    // A multi-customer check scopes to each chosen customer (comma-joined) and shows the union. With
    // no customer chosen yet it loads nothing rather than the org's whole invoice history.
    const split = (p.customer_groups?.length ?? 0) > 0
    const scopes: (string | undefined)[] = split ? (customerRemoteId ? customerRemoteId.split(",") : []) : [customerRemoteId ?? undefined]
    // The check number widens each scope with the check's own invoices billed to other customers.
    const results = await Promise.all(scopes.map((scope: string | undefined) => listAccountingInvoices(scope, p.check_number || checkNumber)))
    if (!shouldApply()) {
      return
    }
    invoicesLoadedForCustomer.current = customerRemoteId

    const by_id = new Map<string, AccountingInvoiceJson>()
    for (const invoicesResult of results) {
      if (invoicesResult.ok) {
        for (const row of invoicesResult.value.data) {
          by_id.set(row.id, row)
        }
      } else {
        toast({ title: "Failed to load accounting invoices", variant: "destructive" })
      }
    }
    const invoiceRows: AccountingInvoiceJson[] = Array.from(by_id.values())
    setAccountingInvoices(invoiceRows)

    const initial: Record<string, string> = {}
    for (const line of p.invoice_assignments) {
      if (line.accounting_invoice_id) {
        initial[line.deduction_id] = line.accounting_invoice_id
      }
    }

    const invoice_kind_lines = p.invoice_assignments.filter((l) => l.kind === "invoice")
    const seeded =
      invoiceRows.length > 0
        ? seed_auto_assignments_for_apply_page(
            invoice_kind_lines.map((l) => ({
              deduction_id: l.deduction_id,
              invoice_number: l.invoice_number,
              deduction_amount: l.deduction_amount,
              po_number: l.po_number,
              settle_amount: l.settle_amount,
            })),
            invoiceRows,
            initial
          )
        : initial
    setAssignments(seeded)
  }

  async function loadData() {
    setIsLoading(true)
    setError(null)
    invoicesLoadedForCustomer.current = undefined

    const checkResult = await api_fetch<CheckDetail>(check_api_path(checkNumber!))
    if (!checkResult.ok || !checkResult.value.data.id) {
      setError("Check not found or has no ID")
      setIsLoading(false)
      return
    }
    const checkData = checkResult.value.data
    setCheckDetail(checkData)
    const checkId = checkData.id
    if (!checkId) {
      setError("Check not found or has no ID")
      setIsLoading(false)
      return
    }
    // Invoices are fetched after the customer is resolved (below) so we only load that customer's
    // invoices — an org can have 100k+ invoices, and shipping them all is what made this page hang.
    const [previewResult, customersResult, accountsResult] = await Promise.all([
      prepareCashApplication(checkId, accountingCheckId ?? undefined),
      listQbCustomers(),
      listLedgerAccounts(true),
    ])

    if (customersResult.ok) {
      setCustomers(customersResult.value.data)
    }
    if (accountsResult.ok) {
      const accounts = accountsResult.value.data
      setLedgerAccounts(accounts)
      // Default deposit account = the org's flagged default (e.g. Undeposited Funds)
      const def = accounts.find((a) => a.is_default_for_payments)
      if (def) {
        setDepositAccountId(def.id)
      }
    }

    if (!previewResult.ok) {
      setError("Failed to prepare cash application preview")
      setIsLoading(false)
      return
    }

    const p = previewResult.value.data
    setPreview(p)

    // Default the payment booking date to the check date.
    if (p.check_date) {
      setPaymentDate(toISODateString(parseISO(p.check_date)))
    }

    // Best-effort customer pre-selection (remote id, name, or deduction source), then scope the
    // invoice fetch to that customer.
    let resolvedCustomerId: string | null = null
    if (customersResult.ok) {
      const auto_customer = find_auto_assigned_qb_customer(
        {
          customer_name_hint: p.customer_name || checkData.customer_name,
          deduction_source: checkData.source,
          customer_remote_id: p.customer_remote_id,
        },
        customersResult.value.data
      )
      if (auto_customer) {
        resolvedCustomerId = auto_customer.remote_id
      }
    }
    setCustomerId(resolvedCustomerId)

    // A multi-customer check opens with the customers saved on the last apply.
    const groups = p.customer_groups ?? []
    const seeded_groups = groups.length > 0 && customersResult.ok ? seed_group_customers(groups, customersResult.value.data) : {}
    setGroupCustomers(seeded_groups)

    await load_invoices_and_seed(groups.length > 0 ? chosen_customer_ids(groups, seeded_groups).join(",") || null : resolvedCustomerId, p)

    setIsLoading(false)
  }

  async function handleApply(create_missing_items = false) {
    if (!preview) {
      return
    }
    if (split_mode ? !all_groups_assigned(customer_groups, groupCustomers) : !customerId) {
      setApplyError(
        split_mode
          ? `Select a ${preview.accounting_system} customer for every vendor account before applying.`
          : `Select a ${preview.accounting_system} customer before applying.`
      )
      return
    }
    if (unassignedCount > 0) {
      setApplyError(
        build_unassigned_invoices_error(
          invoiceLines.map((l: InvoiceAssignmentLine) => ({ invoice_number: l.invoice_number, hint: unassigned_hints.get(l.deduction_id) ?? null }))
        )
      )
      return
    }
    if (coding_issue_message) {
      setApplyError(coding_issue_message)
      return
    }
    if (payment_balance_error) {
      setApplyError(payment_balance_error)
      return
    }
    if (has_short_pays && !acceptShortPay) {
      setApplyError("Pick how to handle the short pay before applying, or adjust the coding so the sides balance.")
      return
    }
    if (additional_discount_pending || discount_reduction_pending) {
      setApplyError("Confirm the additional-discount checkbox before applying, or adjust the coding so the sides balance.")
      return
    }
    if (overpaid_error) {
      setApplyError(overpaid_error)
      return
    }
    if (gl_code_issues.length > 0) {
      setApplyError(gl_code_issues_message)
      return
    }

    setApplyError(null)
    setMissingItems([])
    setIsConfirming(true)

    const invoiceAssignments: InvoiceAssignmentInput[] = Object.entries(assignments)
      .filter((entry): entry is [string, string] => typeof entry[1] === "string" && entry[1].length > 0)
      // A line left unapplied settles no invoice; the server refuses one sent both ways.
      .filter(([deductionId]) => !unappliedIds.has(deductionId))
      .map(([deductionId, accountingInvoiceId]) => ({
        deduction_id: deductionId,
        accounting_invoice_id: accountingInvoiceId,
      }))

    // Every group on one customer is an ordinary single-customer apply.
    const group_choice = split_mode ? build_group_selections(customer_groups, groupCustomers, customers) : null
    const apply_customer_id = group_choice ? (group_choice.single_customer_id ?? "") : customerId!
    const customer = customers.find((c) => c.remote_id === apply_customer_id)
    const depositAccount = ledgerAccounts.find((a) => a.id === depositAccountId)

    const request: ApplyCashApplicationRequest = {
      check_id: preview.check_id,
      customer_id: apply_customer_id,
      customer_name: customer?.display_name,
      customer_groups: group_choice && group_choice.customer_groups.length > 0 ? group_choice.customer_groups : undefined,
      deposit_account_remote_id: depositAccount?.remote_id,
      payment_date: paymentDate || undefined,
      invoice_assignments: invoiceAssignments,
      create_missing_items: create_missing_items || undefined,
      allow_short_pay: has_short_pays && acceptShortPay && !book_short_pay_as_deduction ? true : undefined,
      short_pay_as_deduction: book_short_pay_as_deduction && acceptShortPay ? true : undefined,
      posting_dates: posting_date_overrides.length > 0 ? posting_date_overrides : undefined,
      additional_discount_account_id:
        (additional_discount_active || discount_reduction_active) && !discount_on_payment ? additional_discount_account!.id : undefined,
      additional_discount_on_payment: (additional_discount_active || discount_reduction_active) && discount_on_payment ? true : undefined,
      book_bi_allowance: bi_allowance_lines.length > 0 ? true : undefined,
      unapplied_deduction_ids:
        unapplied_lines.length > 0 ? unapplied_lines.map((l: InvoiceAssignmentLine) => l.deduction_id) : undefined,
    }

    const result = await applyCashApplication(request)
    setIsConfirming(false)

    if (result.ok) {
      const data = result.value.data
      if (data.payment_created) {
        if (data.warning) {
          // Only one toast shows at a time, so the warning replaces the success message; it stays
          // up for a minute and is kept in the notifications list.
          toast({ title: "Cash applied, but invoices were left open", description: data.warning, variant: "destructive", duration: 60000 })
        } else {
          capture_event("cash_application_applied", {
            accounting_system: preview.accounting_system,
            credit_memo_lines: credit_memo_lines.length,
          })
          toast({
            title: `Cash applied to ${preview.accounting_system}`,
            // A check of pure invoice payments publishes no memo — the payment settles it alone.
            description:
              (credit_memo_lines.length > 0 ? "Credit memo and payment created." : "Payment created.") +
              (unapplied_cash_cents > 0
                ? ` ${exactCurrencyFormatter(unapplied_cash_cents / 100)} left unapplied on the customer's account.`
                : ""),
          })
        }
        setLocation(`/accounting/checks`)
      } else {
        // Partial success: credit memo created but payment failed.
        setApplyError(data.error ?? "The payment could not be created. Please retry.")
        setMissingItems(data.missing_product_service_items ?? [])
      }
    } else {
      setApplyError(result.error.message || "Failed to apply cash. Please try again or contact support.")
    }
  }

  /** Move `is_default_for_payments` onto the account the user just made the org default. */
  function handle_default_deposit_account_change(accountId: string) {
    setLedgerAccounts((prev: LedgerAccountJson[]) =>
      prev.map((a) => ({ ...a, is_default_for_payments: a.id === accountId }))
    )
  }

  function handle_assignment_change(deduction_id: string, accounting_invoice_id: string | null) {
    setAssignments((prev: Record<string, string>) => {
      const next = { ...prev }
      if (accounting_invoice_id) {
        next[deduction_id] = accounting_invoice_id
      } else {
        delete next[deduction_id]
      }
      return next
    })
  }

  /**
   * Leave an invoice line's cash unapplied on the customer's account, or take that back. Leaving it
   * drops any invoice picked for it — it settles none.
   */
  function handle_unapplied_change(deduction_id: string, leave_unapplied: boolean) {
    setUnappliedIds((prev: Set<string>) => {
      const next = new Set(prev)
      if (leave_unapplied) {
        next.add(deduction_id)
      } else {
        next.delete(deduction_id)
      }
      return next
    })
    if (leave_unapplied) {
      handle_assignment_change(deduction_id, null)
    }
  }

  function toggle_cm_expanded(key: string) {
    set_expanded_cm_keys((prev: Set<string>) => {
      const n = new Set(prev)
      if (n.has(key)) {
        n.delete(key)
      } else {
        n.add(key)
      }
      return n
    })
  }

  const transaction_by_deduction_id = useMemo(() => {
    const m = new Map<string, TransactionJson>()
    if (!checkDetail?.transactions) {
      return m
    }
    for (const t of checkDetail.transactions) {
      m.set(t.id, t)
    }
    return m
  }, [checkDetail])

  const qb_customer_recommendation_context = useMemo(
    () => ({
      customer_name_hint: preview?.customer_name || checkDetail?.customer_name,
      deduction_source: checkDetail?.source,
      customer_remote_id: preview?.customer_remote_id,
    }),
    [preview, checkDetail]
  )

  // Why each still-unassigned invoice line has no invoice (a PO placeholder, or nothing synced under
  // that number), indexed once per invoice list rather than scanned per line per render.
  const invoice_lookup = useMemo(() => build_invoice_lookup(accountingInvoices), [accountingInvoices])
  const unassigned_hints = useMemo(() => {
    const hints = new Map<string, string>()
    if (!preview || invoicesLoading) {
      return hints
    }
    // A split check loads a vendor account's invoices only once it has a customer; until then the
    // empty list says nothing about whether an invoice exists.
    const awaiting_customers = split_mode && !all_groups_assigned(customer_groups, groupCustomers)
    for (const line of preview.invoice_assignments) {
      if (line.kind !== "invoice" || assignments[line.deduction_id] || unappliedIds.has(line.deduction_id)) {
        continue
      }
      const hint = awaiting_customers
        ? choose_group_customers_hint(preview.accounting_system)
        : describe_unassigned_invoice_line(line, invoice_lookup, preview.accounting_system)
      if (hint) {
        hints.set(line.deduction_id, hint)
      }
    }
    return hints
  }, [preview, invoicesLoading, assignments, unappliedIds, invoice_lookup, split_mode, customer_groups, groupCustomers])

  const sorted_qb_customers = useMemo(
    () => sort_qb_customers_by_recommendation(customers, qb_customer_recommendation_context),
    [customers, qb_customer_recommendation_context]
  )

  if (isLoading) {
    return (
      <div class="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error || !preview || !checkDetail) {
    return (
      <div class="container mx-auto px-4 py-10 max-w-3xl">
        <div class="text-center">
          <p class="text-red-600">{error || "Failed to load preview"}</p>
          <Link to={check_detail_path(checkNumber!)}>
            <Button variant="outline" class="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Check
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  // The two sides of the apply, by the same rule the bulk apply uses (`compute_apply_balance`).
  const {
    invoice_lines: invoiceLines,
    unapplied_lines,
    unapplied_cash_cents,
    unassigned_count: unassignedCount,
    accounting_invoice_by_id,
    credited_by_invoice,
    selected_invoice_total_cents,
    check_cash_cents,
    coded_credit_memo_total_cents,
    early_pay_discount_cents,
    base_applied_total_cents,
    payment_balance_delta_cents,
  } = compute_apply_balance(preview, assignments, accountingInvoices, unappliedIds)
  // Every invoice line as the remittance lists it: the ones matched to invoices and the ones left
  // unapplied share one table, in the remittance's order.
  const invoice_table_lines = preview.invoice_assignments.filter((l: InvoiceAssignmentLine) => l.kind === "invoice")
  const unapplied_cash_note = build_unapplied_cash_note(unapplied_lines, check_cash_cents, preview.accounting_system)
  const nonInvoiceLines = preview.invoice_assignments.filter((l: InvoiceAssignmentLine) => l.kind !== "invoice")
  // Deductions the accounting system already applied to one of these invoices as a credit note
  // (the brand's own CN-00000117 on SI-00004219, listed again on Thrive's advice). The server left
  // them off the memo and the tables; here they come off each invoice's charge, so the sides
  // balance on what the system has due and the outstanding-balance guard agrees.
  const already_credited: AlreadyCreditedLine[] = preview.already_credited ?? []
  const already_credited_note = build_already_credited_note(already_credited, preview.accounting_system)
  // Early-pay discounts taken on the payment itself ("Disc. Taken") settle part of each gross
  // invoice, so they fund the application alongside cash and the credit memo (which excludes them
  // in this mode). Only assigned invoice lines count — the assignment is editable here.
  // The gap the additional-discount lines close is computed below from this delta; they are
  // added to the memo total once accepted.
  // Per-deduction (Xero) mode surfaces a date + invoice on each line; show those columns when present.
  const per_deduction_credit_memo = preview.credit_memo_lines.some((line) => line.execution_date || line.reference)
  // An uncoded transaction's cash is inside the check total while contributing nothing to the credit
  // memo, so it always makes the sides unbalanced. It is the cause, the balance gap is the symptom —
  // report it instead, or the message sends the user off to re-check their invoice selection.
  const uncoded_transactions = preview.uncoded_transactions ?? []
  const uncoded_transactions_message = build_uncoded_transactions_error(uncoded_transactions)
  const missing_product_group_note = build_missing_product_group_note(preview.credit_memo_lines, preview.product_group_field)
  const missing_chargeback_type_note = build_missing_chargeback_type_note(preview.credit_memo_lines, preview.chargeback_type_field)
  // The mirror image: a transaction coded for more than the check carries for it (a repayment coded
  // at its gross) also unbalances the sides, by dollars nobody remitted. Same treatment.
  const overcoded_transactions = preview.overcoded_transactions ?? []
  const overcoded_transactions_message = build_overcoded_transactions_error(overcoded_transactions)
  const coding_issue_message = uncoded_transactions_message || overcoded_transactions_message
  const coding_is_clean = uncoded_transactions.length === 0 && overcoded_transactions.length === 0
  // Every line on this check, so an invoice link from a line on *another* check (the other
  // installment of an invoice paid across checks) reads as a partial payment rather than a
  // reservation.
  const check_deduction_ids = new Set(preview.invoice_assignments.map((l: InvoiceAssignmentLine) => l.deduction_id))
  const link_summaries = summarize_invoice_links(accountingInvoices, check_deduction_ids)
  // What each invoice line settles, so a second line of this check can share an invoice the first
  // settles only in part (UNFI's SI00004292 + SI00004292A for Compostic's SI-00004292).
  const line_settle_cents = line_settle_cents_by_id(preview.invoice_assignments)
  // UNFI `PP` lines: an advance shows below as its invoice's payment (numbered by the stem, not the
  // `PP` number on the remittance) and a resolved reversal drops out with the line it settled — both
  // need explaining, or the tables silently disagree with the remittance the user is holding. A
  // reversal with no matching invoice line blocks applying, same as the server will.
  const prepayments = preview.prepayments ?? []
  // A repayment of a rebilled deduction is listed as the rebill invoice's payment; say so under the
  // line, since the remittance the user is holding names the original invoice.
  const rebill_settlements = preview.rebill_settlements ?? []
  const unresolved_prepayments_message = build_unresolved_prepayments_error(prepayments)
  const prepayment_summary = build_prepayment_summary(prepayments)
  // A check on which the customer pays money back: the memo nets negative and needs a sales invoice,
  // which the apply creates (Xero, QuickBooks, NetSuite). Lay out what it will do for review; block
  // on other providers, which have no such path.
  const repayment_invoice = preview.repayment_invoice ?? null
  const repayment_invoice_row = repayment_invoice
    ? { ...repayment_invoice, accounting_system: preview.accounting_system, customer_name: preview.customer_name }
    : null
  const repayment_invoice_note = build_repayment_invoice_note(repayment_invoice_row)
  const repayment_invoice_steps = build_repayment_invoice_steps(repayment_invoice_row)
  const repayment_invoice_unsupported = repayment_invoice !== null && !repayment_invoice.supported
  // When the invoices exceed cash + credit memo and the gap is exactly the per-invoice shorts
  // (accounting total vs settle amount), offer apply-anyway with the gap flagged as short pays
  // instead of hard-blocking. Any other imbalance stays a hard error.
  // Uncoded transactions distort the delta, so a match against the per-invoice shorts here would be
  // a coincidence, not a short pay — don't offer apply-anyway until the coding is real.
  // An invoice paid across several checks (UNFI's SI00003941 / SI00003941A for Compostic's
  // SI-00003941) is the same shape with the remainder on a check we can name: a partial payment,
  // applied without confirmation. Only the part nobody has remitted is a short pay.
  const is_xero = preview.accounting_system === "Xero"
  const raw_settlement_breakdown =
    unassignedCount === 0 && coding_is_clean
      ? compute_settlement_breakdown(invoiceLines, assignments, accounting_invoice_by_id, link_summaries, payment_balance_delta_cents, credited_by_invoice)
      : null
  // KeHE's BI allowance comes off each invoice's gap first — a known write-off, never a short pay
  // or a discount — so everything below sees only what is left. The server books the same lines.
  const bi_allowance = preview.bi_allowance ?? null
  const bi_plan = bi_allowance
    ? plan_bi_allowance_rows(raw_settlement_breakdown, accounting_invoice_by_id, assignments, bi_allowance.percentage)
    : { rows: [], remaining: raw_settlement_breakdown }
  const settlement_breakdown = bi_plan.remaining
  const bi_allowance_lines = bi_allowance ? build_bi_allowance_lines(bi_plan.rows, bi_allowance) : []
  const bi_allowance_cents_total = bi_plan.rows.reduce((sum: number, row) => sum + row.booked_cents, 0)
  const bi_allowance_note = bi_allowance ? build_bi_allowance_note(bi_plan.rows, bi_allowance) : null
  // Everywhere but Xero the same per-invoice gap can instead be closed by booking it as an
  // additional discount: Walmart takes its 2% early pay off the pre-allowance subtotal, so its
  // remittance discount under-states what it kept by a few dollars per invoice and no coding change
  // can balance the check. One credit memo line per invoice to the chosen account (the org's
  // early-pay account by default); the server recomputes the gaps and refuses anything wider than a
  // discount.
  const discount_rows = is_xero ? null : additional_discount_rows(settlement_breakdown, accounting_invoice_by_id, assignments)
  const discount_total_cents = (discount_rows ?? []).reduce((sum: number, row) => sum + row.short_cents, 0)
  // Short pay is the fallback, never a second offer alongside the discount — see
  // `short_pay_offer_rows`.
  const settlement_rows = short_pay_offer_rows(settlement_breakdown, discount_rows, preview.accounting_system)
  const short_pay_rows = settlement_rows === null ? null : settlement_rows.filter((row) => row.short_cents > 0)
  const short_pay_total_cents = (short_pay_rows ?? []).reduce((sum: number, row) => sum + row.short_cents, 0)
  const has_short_pays = short_pay_rows !== null && short_pay_rows.length > 0
  const short_pay_deduction_available = can_book_short_pay_deduction(settlement_rows, {
    open_deductions_account: preview.open_deductions_account,
    split_mode,
    bi_allowance_line_count: bi_allowance_lines.length,
  })
  const book_short_pay_as_deduction = has_short_pays && short_pay_deduction_available && shortPayAsDeduction
  const partial_payment_note = settlement_rows === null ? null : build_partial_payment_note(settlement_rows, preview.accounting_system)
  // An invoice settled past its total (a customer trueing up its own rounding — Walmart's reason 100
  // "debit balance write off") has its excess spent on the other invoices, so say so rather than
  // showing shorts that quietly disagree with the remittance.
  // Read from the unfiltered breakdown, so the note shows on the additional-discount path too.
  const over_settlement_note = build_over_settlement_note(settlement_breakdown ?? [])
  // Where the gap goes: onto each invoice's "Disc. Taken" when early pay already rides on the
  // payment, otherwise onto credit memo lines for the chosen account. Same economic item either
  // way, so an org that takes early pay on the payment takes the extra there too rather than
  // splitting one discount across a payment field and a credit memo.
  const discount_on_payment = preview.early_pay_on_payment === true
  const additional_discount_account_id = additionalDiscountAccountId ?? preview.early_pay_account_id ?? null
  const additional_discount_account = ledgerAccounts.find((a: LedgerAccountJson) => a.id === additional_discount_account_id)
  const additional_discount_active =
    discount_rows !== null && acceptAdditionalDiscount && (discount_on_payment || additional_discount_account !== undefined)
  const additional_discount_lines =
    additional_discount_active && !discount_on_payment ? build_additional_discount_lines(discount_rows!, additional_discount_account!) : []
  // What is left unexplained once the BI allowance lines are on the memo.
  const balance_delta_after_bi_cents = payment_balance_delta_cents - bi_allowance_cents_total
  // The opposite shape: the check settles its invoices net *over* their totals (Walmart's reason
  // 100 write-offs outweighing its shorts), so nothing is short and the excess comes off the
  // over-settled invoices' early pay instead — their "Disc. Taken" when early pay rides on the
  // payment, otherwise negative credit memo lines (NetSuite only; its memo takes lines of either
  // sign). Super Coffee's 4544294 was blocked in credit-memo mode until the second path existed.
  const discount_reduction_on_memo = !discount_on_payment && preview.accounting_system === "NetSuite"
  const discount_reduction_rows =
    (discount_on_payment || discount_reduction_on_memo) && !is_xero && unassignedCount === 0 && coding_is_clean
      ? payment_discount_reduction_rows(
          discount_on_payment
            ? invoiceLines
            : invoiceLines.map((line: InvoiceAssignmentLine) => ({ ...line, early_pay_discount: remittance_early_pay_discount(line) })),
          assignments,
          accounting_invoice_by_id,
          balance_delta_after_bi_cents
        )
      : null
  const discount_reduction_total_cents = (discount_reduction_rows ?? []).reduce((sum: number, row) => sum + row.reduce_cents, 0)
  const discount_reduction_active =
    discount_reduction_rows !== null && acceptAdditionalDiscount && (discount_on_payment || additional_discount_account !== undefined)
  const discount_reduction_pending = discount_reduction_rows !== null && !discount_reduction_active
  const discount_reduction_lines =
    discount_reduction_active && discount_reduction_on_memo ? build_discount_reduction_lines(discount_reduction_rows!, additional_discount_account!) : []
  // The memo as it will post: the coded lines plus the BI allowance and additional-discount lines.
  const credit_memo_lines: CreditMemoPreviewLine[] =
    additional_discount_lines.length > 0 || bi_allowance_lines.length > 0 || discount_reduction_lines.length > 0
      ? [...preview.credit_memo_lines, ...bi_allowance_lines, ...additional_discount_lines, ...discount_reduction_lines]
      : preview.credit_memo_lines
  const additional_discount_pending = discount_rows !== null && !additional_discount_active
  const credit_memo_total_cents =
    coded_credit_memo_total_cents +
    bi_allowance_cents_total +
    [...additional_discount_lines, ...discount_reduction_lines].reduce(
      (sum: number, line: CreditMemoPreviewLine) => sum + amount_to_cents(line.amount),
      0
    )
  const payment_balance_error =
    unassignedCount === 0 &&
    coding_is_clean &&
    balance_delta_after_bi_cents !== 0 &&
    settlement_rows === null &&
    discount_rows === null &&
    discount_reduction_rows === null
      ? build_payment_balance_error({
          selected_invoice_total_cents,
          check_cash_cents,
          credit_memo_total_cents,
          early_pay_discount_cents,
          unapplied_cash_cents,
          applied_total_cents: base_applied_total_cents + bi_allowance_cents_total,
          delta_cents: balance_delta_after_bi_cents,
          // A resolved UNFI prepayment rewrites lines, so its check's raw net is not comparable.
          // Lines the system already applied are off the tables but on the remittance, so they
          // count toward what its lines add up to.
          remittance_lines_net_cents:
            prepayments.length === 0
              ? preview.invoice_assignments.reduce(
                  (sum: number, line: InvoiceAssignmentLine) => sum + amount_to_cents(line.deduction_amount),
                  0
                ) - already_credited.reduce((sum: number, line: AlreadyCreditedLine) => sum + amount_to_cents(line.amount), 0)
              : undefined,
        })
      : null

  // Selected invoices that are already (partly) paid in the accounting system can't take the payment.
  // Xero/NetSuite reject over-application; catch it here so the user re-matches before applying.
  // Once per invoice, however many lines pay it.
  const selected_invoices = dedupe_invoices_by_id(
    invoiceLines
      .map((line: InvoiceAssignmentLine) => accounting_invoice_by_id.get(assignments[line.deduction_id] ?? ""))
      .filter((inv): inv is AccountingInvoiceJson => inv !== undefined)
  )
  const overpaid_issues = find_overpaid_invoices(selected_invoices, credited_by_invoice)
  const overpaid_error = overpaid_issues.length > 0 ? build_overpaid_invoices_error(overpaid_issues, preview.accounting_system) : null

  // Xero only: deductions coded to accounts without a valid Xero Code — applying would be rejected.
  const gl_code_issues = preview.gl_code_issues ?? []
  const gl_code_issues_message =
    gl_code_issues.length > 0
      ? `These deductions are coded to accounts that ${preview.accounting_system} won't accept (missing or archived): ${gl_code_issues.join(
          ", "
        )}. In ${preview.accounting_system}, give each account a Code (Accounting → Advanced → Chart of Accounts), re-sync the Chart of Accounts, then re-code the deduction.`
      : ""

  // Xero period locks: a deduction's execution date comes from the distributor's backup, so a check
  // paid this month routinely carries lines dated in a month the brand has already closed and locked
  // in Xero. Xero has no separate posting date and the API gets no advisor override, so such a note
  // can only book in an open period — by default the first day of the first fully open month.
  // Resolve where each line lands; the server does the same and clamps anything still locked, so
  // this is display + the user's override, never the only guard.
  const xero_lock_date = preview.xero_lock_date
  const locked_dates = locked_execution_dates(preview.credit_memo_lines, xero_lock_date)
  // The picker's floor is the day after the lock, not the default — a user who wants a date inside
  // the part-locked month may still pick one.
  const first_open_date = xero_lock_date ? first_postable_date(xero_lock_date) : undefined
  const posting_date_overrides = build_posting_date_overrides(preview.credit_memo_lines, xero_lock_date, postingDates)
  const moved_note_count = preview.credit_memo_lines.filter((line: CreditMemoPreviewLine) =>
    resolve_posting_date(
      line.execution_date,
      xero_lock_date,
      line.execution_date ? postingDates[line.execution_date] : undefined
    )
  ).length

  // Why Apply is off, in words. Rendered above the button so the reason is never a guess; the
  // click handler's own messages only ever ran on an enabled button.
  const blockers = apply_blockers({
    accounting_system: preview.accounting_system,
    customer_missing: split_mode ? !all_groups_assigned(customer_groups, groupCustomers) : customerId === null,
    split_mode,
    unassigned_invoice_count: unassignedCount,
    coding_issue_message: coding_is_clean ? "" : coding_issue_message,
    unresolved_prepayments_message,
    payment_balance_error,
    short_pay_undecided: has_short_pays && !acceptShortPay,
    discount_undecided: additional_discount_pending || discount_reduction_pending,
    overpaid_error,
    gl_code_issues_message,
    repayment_invoice_unsupported,
  })
  const can_apply = !isConfirming && blockers.length === 0

  const card_title_class = "flex items-center gap-2.5 text-lg font-semibold tracking-tight leading-snug"
  const card_title_icon_class = "size-5 shrink-0 text-muted-foreground mt-px"

  return (
    <div class="container mx-auto px-4 py-10 max-w-5xl space-y-8 pb-24">
      <div class="flex items-center gap-4">
        <Link to={check_detail_path(checkNumber!)}>
          <Button variant="ghost" size="icon" class="h-10 w-10 shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div class="min-w-0">
          <h1 class="text-2xl font-bold tracking-tight text-slate-900">Apply Cash</h1>
          <p class="text-slate-600 mt-0.5 text-sm sm:text-base">Create the credit memo and payment in {preview.accounting_system}</p>
        </div>
      </div>

      <Card class="shadow-sm border-slate-200/80">
        <CardHeader class="space-y-1 pb-4">
          <CardTitle className={card_title_class}>
            <DollarSign className={card_title_icon_class} />
            Accounting Check
          </CardTitle>
          <CardDescription>Payment from accounting system</CardDescription>
        </CardHeader>
        <CardContent>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8">
            <div>
              <div class="text-xs font-medium uppercase tracking-wide text-slate-500">Check number</div>
              <div class="mt-1 font-semibold text-slate-900 tabular-nums">{preview.check_number}</div>
            </div>
            <div>
              <div class="text-xs font-medium uppercase tracking-wide text-slate-500">Customer</div>
              <div class="mt-1 font-medium text-slate-900">
                {preview.customer_name || checkDetail?.customer_name || "—"}
              </div>
            </div>
            <div>
              <div class="text-xs font-medium uppercase tracking-wide text-slate-500">Date</div>
              <div class="mt-1 font-medium text-slate-900">{formatDate(preview.check_date)}</div>
            </div>
            <div>
              <div class="text-xs font-medium uppercase tracking-wide text-slate-500">Amount</div>
              <div class="mt-1 text-lg font-semibold text-slate-900 tabular-nums">
                {exactCurrencyFormatter(preview.check_amount)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {prepayment_summary.length > 0 ? (
        <PrepaymentLinesCard
          rows={prepayment_summary}
          titleClass={card_title_class}
          iconClass={card_title_icon_class}
          blocking={unresolved_prepayments_message}
        />
      ) : null}

      {already_credited.length > 0 ? (
        <AlreadyCreditedCard
          rows={already_credited}
          note={already_credited_note ?? ""}
          accountingSystem={preview.accounting_system}
          titleClass={card_title_class}
          iconClass={card_title_icon_class}
        />
      ) : null}

      {(preview.price_true_ups ?? []).length > 0 ? (
        <PriceTrueUpCard
          rows={preview.price_true_ups ?? []}
          customerName={preview.customer_name || "The customer"}
          accountingSystem={preview.accounting_system}
          titleClass={card_title_class}
          iconClass={card_title_icon_class}
        />
      ) : null}

      {(preview.closed_invoice_washes ?? []).length > 0 ? (
        <ClosedInvoiceWashCard
          rows={preview.closed_invoice_washes ?? []}
          customerName={preview.customer_name || "The customer"}
          accountingSystem={preview.accounting_system}
          titleClass={card_title_class}
          iconClass={card_title_icon_class}
        />
      ) : null}

      {invoice_table_lines.length > 0 ? (
        <Card class="shadow-sm border-slate-200/80">
          <CardHeader class="space-y-1 pb-4">
            <CardTitle className={card_title_class}>
              <FileText className={card_title_icon_class} />
              Invoice assignments
            </CardTitle>
            <CardDescription>
              {invoicesLoading ? (
                <span class="inline-flex items-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading invoices…
                </span>
              ) : unassignedCount === 0 ? (
                "All invoices have been assigned"
              ) : (
                `${unassignedCount} invoice${unassignedCount !== 1 ? "s" : ""} need assignment`
              )}
            </CardDescription>
          </CardHeader>
          <CardContent class="pt-0">
            <div class="rounded-md border border-slate-200 overflow-hidden">
              <div class="max-h-[min(28rem,55vh)] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow class="bg-slate-50/90 hover:bg-slate-50/90">
                      <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3">Invoice #</TableHead>
                      <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3">Amount</TableHead>
                      <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3 min-w-[12rem] text-right">
                        Accounting invoice
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoice_table_lines.map((line: InvoiceAssignmentLine) => {
                      const { reserved, shared_room_cents } = plan_invoice_reservations(
                        accountingInvoices,
                        assignments,
                        line.deduction_id,
                        check_deduction_ids,
                        line_settle_cents
                      )
                      const advance = advance_behind_line(prepayments, line.deduction_id)
                      const rebill_settlement = rebill_settlement_behind_line(rebill_settlements, line.deduction_id)
                      const is_unapplied = unappliedIds.has(line.deduction_id)
                      // Offered where it is the way out: nothing is picked, or what is picked is
                      // already paid in the accounting system and cannot take the payment.
                      const picked = accounting_invoice_by_id.get(assignments[line.deduction_id] ?? "")
                      const picked_cannot_take_payment = picked !== undefined && find_overpaid_invoices([picked], credited_by_invoice).length > 0
                      const offer_unapplied =
                        !is_unapplied && can_leave_line_unapplied(preview, line) && (picked === undefined || picked_cannot_take_payment)
                      return (
                        <TableRow key={line.deduction_id} class="align-top">
                          <TableCell class="px-4 py-3 font-mono text-sm font-medium text-slate-900">
                            {line.invoice_number}
                            {advance ? (
                              <div class="mt-0.5 font-sans text-xs font-normal text-slate-500">
                                Paid early as <span class="font-mono">{advance.invoice_number}</span> — UNFI's early payment of this
                                invoice, net of the terms discount
                              </div>
                            ) : null}
                            {rebill_settlement ? (
                              <div class="mt-0.5 max-w-sm font-sans text-xs font-normal text-slate-500">
                                {build_rebill_settlement_note(rebill_settlement)}
                              </div>
                            ) : null}
                            {!is_unapplied && unassigned_hints.has(line.deduction_id) ? (
                              <div class="mt-0.5 max-w-xs font-sans text-xs font-normal text-amber-700">
                                {unassigned_hints.get(line.deduction_id)}
                              </div>
                            ) : null}
                          </TableCell>
                          <TableCell class="px-4 py-3 tabular-nums text-slate-800">
                            {exactCurrencyFormatter(line.deduction_amount)}
                          </TableCell>
                          <TableCell class="px-4 py-3">
                            {is_unapplied ? (
                              <div class="flex w-full items-center justify-end gap-3 min-w-0">
                                <span class="text-sm text-slate-700">
                                  Unapplied: stays on the customer's account in {preview.accounting_system}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  class="h-7 shrink-0 px-2 text-xs"
                                  onClick={() => handle_unapplied_change(line.deduction_id, false)}
                                >
                                  Match to an invoice instead
                                </Button>
                              </div>
                            ) : (
                              <div class="flex w-full flex-col items-end gap-1 min-w-0">
                                <AccountingInvoicePicker
                                  invoices={accountingInvoices}
                                  value={assignments[line.deduction_id] ?? null}
                                  onChange={(id) => handle_assignment_change(line.deduction_id, id)}
                                  recommendationContext={{
                                    invoice_number: line.invoice_number,
                                    amount_dollars: line.deduction_amount,
                                    po_number: line.po_number,
                                  }}
                                  reservedByOtherRows={reserved}
                                  sharedRoomCents={shared_room_cents}
                                  linkSummaries={link_summaries}
                                  className="w-full max-w-md"
                                />
                                {offer_unapplied ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    class="h-7 px-2 text-xs text-slate-600"
                                    onClick={() => handle_unapplied_change(line.deduction_id, true)}
                                  >
                                    {picked_cannot_take_payment
                                      ? `${picked.invoice_number} is already paid — leave this payment unapplied on the customer's account`
                                      : "Leave unapplied on the customer's account"}
                                  </Button>
                                ) : null}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
            {unapplied_cash_note ? <p class="mt-3 text-sm text-slate-700">{unapplied_cash_note}</p> : null}
          </CardContent>
        </Card>
      ) : null}

      {nonInvoiceLines.length > 0 ? (
        <Card class="shadow-sm border-slate-200/80">
          <CardHeader class="space-y-1 pb-4">
            <CardTitle className={card_title_class}>
              <List className={card_title_icon_class} />
              Other transactions
            </CardTitle>
            <CardDescription>Deductions, prepayments, and repayments</CardDescription>
          </CardHeader>
          <CardContent class="pt-0">
            <div class="rounded-md border border-slate-200 overflow-hidden">
              <div class="max-h-[min(24rem,50vh)] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow class="bg-slate-50/90 hover:bg-slate-50/90">
                      <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3">Invoice #</TableHead>
                      <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3">Type</TableHead>
                      <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3 text-right">
                        Amount
                      </TableHead>
                      <TableHead class="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-4 py-3 min-w-[12rem]">
                        GL / splits
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nonInvoiceLines.map((line: InvoiceAssignmentLine) => (
                      <TableRow key={line.deduction_id}>
                        <TableCell class="px-4 py-3 font-mono text-sm text-slate-900">
                          <span class="inline-flex items-center gap-1.5">
                            {line.invoice_number}
                            <AccountingDetailLink deductionId={line.deduction_id} />
                          </span>
                        </TableCell>
                        <TableCell class="px-4 py-3 capitalize text-slate-700">{line.kind}</TableCell>
                        <TableCell class="px-4 py-3 text-right tabular-nums text-slate-800">
                          {exactCurrencyFormatter(line.deduction_amount)}
                        </TableCell>
                        <TableCell class="px-4 py-3">
                          <OtherTransactionSplitsCell transaction={transaction_by_deduction_id.get(line.deduction_id)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {credit_memo_lines.length > 0 ? (
        <Card class="shadow-sm border-slate-200/80">
          <CardHeader class="space-y-1 pb-4">
            <CardTitle className={card_title_class}>
              <Layers className={card_title_icon_class} />
              Credit memo lines
            </CardTitle>
            <CardDescription>
              {per_deduction_credit_memo
                ? "One credit note per deduction, each with its own execution date and invoice"
                : "GL account breakdown for the credit memo"}
            </CardDescription>
          </CardHeader>
          <CardContent class="pt-0 space-y-4">
            {locked_dates.length > 0 && xero_lock_date ? (
              <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <div class="flex items-start gap-2.5">
                  <Lock className="h-4 w-4 mt-0.5 shrink-0" />
                  <div class="space-y-1.5">
                    <p class="font-medium">
                      {preview.accounting_system} is locked through {formatDate(xero_lock_date)}.
                    </p>
                    <p>
                      {moved_note_count === 1 ? "One credit note falls" : `${moved_note_count} credit notes fall`} inside
                      that closed period, and {preview.accounting_system} rejects any document dated on or before a lock —
                      an integration gets no advisor override. They post on the first day of the first fully open month
                      instead, each carrying its original date in its line description so it stays traceable inside{" "}
                      {preview.accounting_system}. Nothing changes in MarginWiz: the deduction still belongs to the month
                      it came from.
                    </p>
                    <p class="text-amber-800/90">Pick a different date on any line below to move it somewhere else.</p>
                  </div>
                </div>
              </div>
            ) : null}
            <div class="rounded-md border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow class="bg-slate-50/90 hover:bg-slate-50/90">
                    <TableHead class="w-10 py-3" />
                    <TableHead class="py-3">GL account</TableHead>
                    {per_deduction_credit_memo ? <TableHead class="py-3">Date</TableHead> : null}
                    {per_deduction_credit_memo ? <TableHead class="py-3">Invoice</TableHead> : null}
                    <TableHead class="py-3">Description</TableHead>
                    <TableHead class="py-3 text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {credit_memo_lines.map((line: CreditMemoPreviewLine, index: number) => {
                    const cm_key = `${line.gl_account_id}-${index}`
                    const expanded = expanded_cm_keys.has(cm_key)
                    const contributors = collect_splits_for_gl_line(checkDetail.transactions, line.gl_account_id)
                    return (
                      <Fragment key={cm_key}>
                        <TableRow class="align-middle">
                          <TableCell class="py-2 w-10">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              class="h-8 w-8 text-slate-600"
                              onClick={() => toggle_cm_expanded(cm_key)}
                              aria-expanded={expanded}
                              title={expanded ? "Collapse" : "Show related transactions"}>
                              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                          </TableCell>
                          <TableCell class="py-3">
                            <div class="font-semibold text-slate-900 tabular-nums">{line.gl_account_id}</div>
                            <div class="text-sm text-slate-500 mt-0.5">{line.gl_account_name}</div>
                            <LineListFieldValue field_name={preview.product_group_field} value={line.product_group} />
                            <LineListFieldValue field_name={preview.chargeback_type_field} value={line.chargeback_type} />
                            {is_item_based_accounting_system(preview.accounting_system)
                              ? (() => {
                                  const mapped = credit_memo_line_product_service(line, ledgerAccounts)
                                  return (
                                    <div class="text-xs mt-0.5">
                                      <span class="text-slate-400">Item: </span>
                                      {mapped ? (
                                        <span class="text-slate-600 font-medium">{mapped}</span>
                                      ) : (
                                        <span class="text-amber-600">not mapped — GL account id will be used as the item name</span>
                                      )}
                                    </div>
                                  )
                                })()
                              : null}
                          </TableCell>
                          {per_deduction_credit_memo ? (
                            <TableCell class="py-3 text-slate-700 tabular-nums whitespace-nowrap">
                              <PostingDateCell
                                execution_date={line.execution_date}
                                posting_date={resolve_posting_date(
                                  line.execution_date,
                                  xero_lock_date,
                                  line.execution_date ? postingDates[line.execution_date] : undefined
                                )}
                                first_open_date={first_open_date}
                                onChange={(posting_date: string) =>
                                  line.execution_date
                                    ? setPostingDates((prev) => ({ ...prev, [line.execution_date as string]: posting_date }))
                                    : undefined
                                }
                              />
                            </TableCell>
                          ) : null}
                          {per_deduction_credit_memo ? <TableCell class="py-3 text-slate-700">{line.reference || "—"}</TableCell> : null}
                          <TableCell class="py-3 text-slate-700">{line.description || "—"}</TableCell>
                          <TableCell class="py-3 text-right tabular-nums font-medium text-slate-900">
                            {exactCurrencyFormatter(line.amount)}
                          </TableCell>
                        </TableRow>
                        {expanded ? (
                          <TableRow key={`${cm_key}-detail`} class="bg-slate-50/60">
                            <TableCell />
                            <TableCell colSpan={per_deduction_credit_memo ? 5 : 3} class="py-3 pr-4">
                              {contributors.length === 0 ? (
                                <p class="text-sm text-slate-500">No linked splits found for this GL line.</p>
                              ) : (
                                <ul class="space-y-2">
                                  {contributors.map((c, idx) => (
                                    <li
                                      key={`${c.deduction_id}-${idx}`}
                                      class="text-sm border-b border-slate-200/80 last:border-0 pb-2 last:pb-0">
                                      <div class="flex flex-wrap items-baseline justify-between gap-2">
                                        <span class="font-medium text-slate-900 inline-flex items-center gap-1.5">
                                          {c.invoice_ref}{" "}
                                          <span class="text-slate-500 font-normal capitalize">({c.kind})</span>
                                          <AccountingDetailLink deductionId={c.deduction_id} />
                                        </span>
                                        <span class="tabular-nums text-slate-800">{exactCurrencyFormatter(c.amount)}</span>
                                      </div>
                                      {c.memo ? <div class="text-xs text-slate-500 mt-1">{c.memo}</div> : null}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {repayment_invoice && repayment_invoice_note ? (
        <Card class={`shadow-sm ${repayment_invoice_unsupported ? "border-red-200" : "border-slate-200/80"}`}>
          <CardHeader class="space-y-1 pb-4">
            <CardTitle className={card_title_class}>
              <Receipt className={card_title_icon_class} />
              Repayment invoice
            </CardTitle>
            <CardDescription>
              {repayment_invoice_unsupported
                ? "This check can't be applied from here"
                : `MarginWiz creates this invoice when you apply`}
            </CardDescription>
          </CardHeader>
          <CardContent class="pt-0 space-y-4">
            <div
              class={`rounded-md border px-4 py-3 text-sm ${
                repayment_invoice_unsupported ? "border-red-200 bg-red-50 text-red-700" : "border-sky-200 bg-sky-50 text-sky-900"
              }`}>
              {repayment_invoice_note}
              {repayment_invoice_steps.length > 0 ? (
                <ol class="mt-2 list-decimal space-y-1 pl-5">
                  {repayment_invoice_steps.map((step: string) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              ) : null}
            </div>
            {!repayment_invoice_unsupported ? (
              <div class="overflow-x-auto rounded-md border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow class="bg-slate-50">
                      <TableHead class="px-4">Reference</TableHead>
                      <TableHead class="px-4">GL account</TableHead>
                      <TableHead class="px-4 text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {repayment_invoice.lines.map((line, index: number) => (
                      <TableRow key={index}>
                        <TableCell class="px-4 font-mono text-sm">{line.reference ?? repayment_invoice.reference}</TableCell>
                        <TableCell class="px-4 text-sm">
                          {line.gl_account_id}
                          {line.gl_account_name ? ` — ${line.gl_account_name}` : ""}
                          {is_item_based_accounting_system(preview.accounting_system)
                            ? (() => {
                                const mapped = credit_memo_line_product_service(line, ledgerAccounts)
                                return (
                                  <div class="text-xs mt-0.5">
                                    <span class="text-slate-400">Item: </span>
                                    {mapped ? (
                                      <span class="text-slate-600 font-medium">{mapped}</span>
                                    ) : (
                                      <span class="text-amber-600">not mapped — GL account id will be used as the item name</span>
                                    )}
                                  </div>
                                )
                              })()
                            : null}
                        </TableCell>
                        <TableCell class="px-4 text-right tabular-nums">{exactCurrencyFormatter(Number(line.amount))}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow class="bg-slate-50 font-medium">
                      <TableCell class="px-4" colSpan={2}>
                        Invoice total
                      </TableCell>
                      <TableCell class="px-4 text-right tabular-nums">{exactCurrencyFormatter(Number(repayment_invoice.total))}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : repayment_invoice === null ? (
        /* No lines and no repayment invoice: a lockbox check of pure invoice payments. A check whose
           every line became the repayment invoice also has no memo, but its own note explains it. */
        <Card class="shadow-sm border-slate-200/80">
          <CardHeader class="space-y-1 pb-4">
            <CardTitle className={card_title_class}>
              <Layers className={card_title_icon_class} />
              No credit memo
            </CardTitle>
            <CardDescription>
              This check is nothing but invoice payments — there is no deduction to credit, so nothing needs coding. The{" "}
              {preview.accounting_system} payment settles the selected invoices on its own.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {split_mode ? (
        <CustomerGroupsCard
          accounting_system={preview.accounting_system}
          groups={customer_groups}
          lines={preview.invoice_assignments}
          customers={sorted_qb_customers}
          selections={groupCustomers}
          on_select={(key: string, customer_id: string) => setGroupCustomers((prev: Record<string, string>) => ({ ...prev, [key]: customer_id }))}
        />
      ) : null}

      <Card class="shadow-sm border-slate-200/80">
        <CardHeader class="space-y-1 pb-4">
          <CardTitle className={card_title_class}>
            <CreditCard className={card_title_icon_class} />
            Payment details
          </CardTitle>
          <CardDescription>Create the payment in {preview.accounting_system} and link it to the matched invoices</CardDescription>
        </CardHeader>
        <CardContent class="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {split_mode ? null : (
            <div class="space-y-1.5">
              <Label>{preview.accounting_system} customer</Label>
              <AutocompleteSelect
                searchPlaceholder="Search customers..."
                emptyText={`No ${preview.accounting_system} customers synced yet. Run a sync from Settings → Accounting.`}
                values={sorted_qb_customers.map((c) => ({ id: c.remote_id, name: c.display_name }))}
                initial={
                  customerId
                    ? {
                        id: customerId,
                        name:
                          sorted_qb_customers.find((c) => c.remote_id === customerId)?.display_name ?? customerId,
                      }
                    : undefined
                }
                onSelect={(item: SelectItem) => setCustomerId(item.id)}
              />
            </div>
          )}
          <DepositAccountField
            accounts={ledgerAccounts}
            value={depositAccountId}
            onChange={setDepositAccountId}
            onDefaultChange={handle_default_deposit_account_change}
          />
          <div class="space-y-1.5">
            <Label htmlFor="payment-date">Payment date</Label>
            <Input
              id="payment-date"
              type="date"
              value={paymentDate}
              onInput={(e: Event) => setPaymentDate((e.target as HTMLInputElement).value)}
            />
          </div>
        </CardContent>
      </Card>

      {applyError && isXeroReauthError(applyError) ? (
        // A dead Xero connection isn't a retryable apply failure — nothing was written, and the only
        // way forward is re-consenting. Give the button rather than a message to re-read.
        <XeroReconnectNotice message={applyError} />
      ) : applyError ? (
        <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 space-y-3">
          <div>{applyError}</div>
          {missingItems.length > 0 && (
            <div class="flex flex-wrap items-center gap-3 border-t border-red-200 pt-3">
              <span class="text-red-700">
                MarginWiz can create {missingItems.length === 1 ? "this item" : "these items"} in QuickBooks for you
                (a Service item per GL account, posting to that account):{" "}
                <span class="font-medium">{missingItems.join(", ")}</span>
              </span>
              <Button
                size="sm"
                class="bg-green-600 hover:bg-green-700"
                disabled={isConfirming}
                onClick={() => void handleApply(true)}
              >
                {isConfirming
                  ? "Creating…"
                  : `Create ${missingItems.length === 1 ? "item" : `${missingItems.length} items`} in QuickBooks & retry`}
              </Button>
            </div>
          )}
        </div>
      ) : null}
      {!applyError && gl_code_issues.length > 0 ? (
        <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{gl_code_issues_message}</div>
      ) : null}
      {!applyError && preview.xero_backup_attachments_blocked ? (
        <XeroReconnectNotice tone="warning" message={XERO_ATTACHMENTS_RECONNECT_MESSAGE} />
      ) : null}
      {!applyError && overpaid_error ? (
        <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{overpaid_error}</div>
      ) : null}
      {!applyError && missing_product_group_note ? (
        <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{missing_product_group_note}</div>
      ) : null}
      {!applyError && missing_chargeback_type_note ? (
        <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{missing_chargeback_type_note}</div>
      ) : null}
      {!applyError && uncoded_transactions_message ? (
        <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div>{uncoded_transactions_message}</div>
          {/* The row to code, by id: on a UNFI check the uncoded clawback row (`SI00004145`) sits
              beside the PP reversal it came from (`SI00004145PP`), and only one of them codes. */}
          <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {uncoded_transactions.map((row: UncodedTransaction) => (
              <AccountingDetailLink key={row.deduction_id} deductionId={row.deduction_id} label={`Code ${row.invoice_number}`} />
            ))}
          </div>
        </div>
      ) : null}
      {!applyError && overcoded_transactions_message ? (
        <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{overcoded_transactions_message}</div>
      ) : null}
      {!applyError && preview.open_deductions_integration_id ? (
        <OpenDeductionsAccountControl
          integration_id={preview.open_deductions_integration_id}
          accounting_system={preview.accounting_system}
          account={preview.open_deductions_account}
          account_name={preview.open_deductions_account_name}
          scope={preview.open_deductions_scope}
          ledger_accounts={ledgerAccounts}
          onSaved={loadData}
        />
      ) : null}
      {!applyError && preview.dc_plan_error ? (
        <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <p class="font-medium">This check can't be split across its DC customers yet</p>
          <p>{preview.dc_plan_error}</p>
        </div>
      ) : null}
      {!applyError && (preview.dc_groups?.length ?? 0) > 0 ? (
        <div class="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 space-y-2">
          <p class="font-medium">DC-level application: {preview.dc_groups!.length} payments will be created</p>
          <p>
            This check's invoices belong to {preview.dc_groups!.length} different Sage customers, and Sage requires each payment,
            credit memo, and its invoices to share one customer — so applying creates one credit memo and one payment per DC.
            Deductions follow their DC when we can tell (a matching invoice, a split's customer, or the UNFI DC name); the rest is
            spread across the DCs in proportion to their invoice totals.
          </p>
          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead>
                <tr class="border-b border-sky-200 text-left text-sky-700">
                  <th class="py-1 pr-3">Customer</th>
                  <th class="py-1 pr-3 text-right">Invoices</th>
                  <th class="py-1 pr-3 text-right">Invoice total</th>
                  <th class="py-1 pr-3 text-right">DC deductions</th>
                  <th class="py-1 pr-3 text-right">Spread share</th>
                  <th class="py-1 text-right">Cash applied</th>
                </tr>
              </thead>
              <tbody>
                {preview.dc_groups!.map((group) => (
                  <tr key={group.customer_id} class="border-b border-sky-100 last:border-b-0">
                    <td class="py-1 pr-3">{group.customer_name ?? group.customer_id}</td>
                    <td class="py-1 pr-3 text-right">{group.invoice_count}</td>
                    <td class="py-1 pr-3 text-right font-mono">{exactCurrencyFormatter(group.invoice_total)}</td>
                    <td class="py-1 pr-3 text-right font-mono">{exactCurrencyFormatter(group.direct_credit)}</td>
                    <td class="py-1 pr-3 text-right font-mono">{exactCurrencyFormatter(group.spread_credit)}</td>
                    <td class="py-1 text-right font-mono">{exactCurrencyFormatter(group.cash)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      {!applyError && payment_balance_error ? (
        <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {payment_balance_error}
        </div>
      ) : null}
      {!applyError && !payment_balance_error && partial_payment_note ? (
        <div class="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">{partial_payment_note}</div>
      ) : null}
      {!applyError && !payment_balance_error && bi_allowance_note ? (
        <div class="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">{bi_allowance_note}</div>
      ) : null}
      {!applyError && !payment_balance_error && over_settlement_note ? (
        <div class="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">{over_settlement_note}</div>
      ) : null}
      {!applyError && !payment_balance_error && short_pay_rows !== null && has_short_pays ? (
        <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 space-y-3">
          <p>
            {preview.customer_name || "The customer"} paid{" "}
            <span class="font-semibold tabular-nums">{exactCurrencyFormatter(short_pay_total_cents / 100)}</span> less than the
            selected {preview.accounting_system} invoice totals, beyond the coded deductions and early-pay discounts.
          </p>
          <ul class="space-y-1">
            {short_pay_rows.map((row) => (
              <li key={row.deduction_id} class="flex items-baseline gap-2 tabular-nums">
                <span class="font-medium">{row.invoice_number}</span>
                <span>short paid by {exactCurrencyFormatter(row.short_cents / 100)}</span>
              </li>
            ))}
          </ul>
          {short_pay_deduction_available ? (
            <>
              <p class="font-medium">
                Pick what happens to the {exactCurrencyFormatter(short_pay_total_cents / 100)} to apply this check:
              </p>
              <ShortPayChoice
                checked={acceptShortPay && !shortPayAsDeduction}
                onCheckedChange={(checked: boolean) => {
                  setShortPayAsDeduction(!checked)
                  setAcceptShortPay(checked)
                }}
                title="Leave it owed on the invoice"
                when="Use when you expect the rest to be paid on a later check, or you will correct the invoice yourself."
                effect={`Each invoice is settled for what was paid. The ${exactCurrencyFormatter(short_pay_total_cents / 100)} stays due in ${preview.accounting_system} and is flagged as a short pay to follow up on. No deduction is created.`}
              />
              <ShortPayChoice
                checked={acceptShortPay && shortPayAsDeduction}
                onCheckedChange={(checked: boolean) => {
                  setShortPayAsDeduction(true)
                  setAcceptShortPay(checked)
                }}
                title="Dispute it later — book a short pay deduction"
                when={`Use when ${preview.customer_name || "the customer"} owes you this money and the invoices are right.`}
                effect={`Each invoice closes in full in ${preview.accounting_system}. The ${exactCurrencyFormatter(short_pay_total_cents / 100)} parks in the open deductions account (${preview.open_deductions_account}) with the rest of this check's deductions and shows up in Deductions to dispute.`}
              />
            </>
          ) : (
            <>
              <p>
                You can apply anyway: each invoice above is settled for its total minus the short pay, the remainder stays due in{" "}
                {preview.accounting_system}, and the gap is flagged as a short pay to follow up on.
              </p>
              <label class="flex items-start gap-2 cursor-pointer font-medium">
                <Checkbox
                  checked={acceptShortPay}
                  onCheckedChange={(checked: boolean | "indeterminate") => setAcceptShortPay(checked === true)}
                  class="mt-0.5"
                />
                <span>{`Apply anyway and flag ${exactCurrencyFormatter(short_pay_total_cents / 100)} as short pay`}</span>
              </label>
            </>
          )}
        </div>
      ) : null}

      {!applyError && discount_rows !== null ? (
        <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 space-y-3">
          <p>
            {preview.customer_name || "The customer"} settled{" "}
            <span class="font-semibold tabular-nums">{exactCurrencyFormatter(discount_total_cents / 100)}</span> less than the
            selected {preview.accounting_system} invoice totals, beyond the coded deductions and the early-pay discounts on the
            remittance — a customer that takes its early-pay discount off a bigger base than the invoice (Walmart discounts the
            pre-allowance subtotal) lands here.{" "}
            {discount_on_payment
              ? `Take the difference as additional early-pay discount on the payment: it is added to each invoice's "Disc. Taken" and books to the same discount account as the rest of the early pay, so each invoice closes in full.`
              : "Book the difference as an additional discount: one credit memo line per invoice, on the account below, so each invoice closes in full."}
          </p>
          <ul class="space-y-1">
            {discount_rows.map((row) => (
              <li key={row.deduction_id} class="flex items-baseline gap-2 tabular-nums">
                <span class="font-medium">{row.invoice_number}</span>
                <span>short by {exactCurrencyFormatter(row.short_cents / 100)}</span>
              </li>
            ))}
          </ul>
          {discount_on_payment ? null : (
            <DiscountAccountPicker
              accounts={ledgerAccounts}
              selected={additional_discount_account}
              onSelect={(id: string) => setAdditionalDiscountAccountId(id)}
            />
          )}
          <label class="flex items-start gap-2 cursor-pointer font-medium">
            <Checkbox
              checked={acceptAdditionalDiscount}
              disabled={!discount_on_payment && !additional_discount_account}
              onCheckedChange={(checked: boolean | "indeterminate") => setAcceptAdditionalDiscount(checked === true)}
              class="mt-0.5"
            />
            <span>
              {discount_on_payment
                ? `Take ${exactCurrencyFormatter(discount_total_cents / 100)} of additional early-pay discount on the payment`
                : `Add ${discount_rows.length === 1 ? "an additional discount line" : `${discount_rows.length} additional discount lines`} totalling ${exactCurrencyFormatter(discount_total_cents / 100)} to the credit memo`}
            </span>
          </label>
        </div>
      ) : null}

      {!applyError && discount_reduction_rows !== null ? (
        <div class="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 space-y-3">
          <p>
            {preview.customer_name || "The customer"} settled{" "}
            <span class="font-semibold tabular-nums">{exactCurrencyFormatter(discount_reduction_total_cents / 100)}</span> more than
            the selected {preview.accounting_system} invoice totals once the coded deductions and the early-pay discounts on the
            remittance are counted — a customer that pays an invoice past its total and writes the difference back off (Walmart's
            reason 100 "debit balance write off") lands here.{" "}
            {discount_on_payment
              ? `Take that much less early-pay discount on the payment: it comes off each over-paid invoice's "Disc. Taken", so each invoice closes at its total.`
              : "Take that much early-pay discount back off the credit memo: one negative line per over-paid invoice, on the account below, so each invoice closes at its total."}
          </p>
          <ul class="space-y-1">
            {discount_reduction_rows.map((row) => (
              <li key={row.deduction_id} class="flex items-baseline gap-2 tabular-nums">
                <span class="font-medium">{row.invoice_number}</span>
                <span>{exactCurrencyFormatter(row.reduce_cents / 100)} less discount</span>
              </li>
            ))}
          </ul>
          {discount_on_payment ? null : (
            <DiscountAccountPicker
              accounts={ledgerAccounts}
              selected={additional_discount_account}
              onSelect={(id: string) => setAdditionalDiscountAccountId(id)}
            />
          )}
          <label class="flex items-start gap-2 cursor-pointer font-medium">
            <Checkbox
              checked={acceptAdditionalDiscount}
              disabled={!discount_on_payment && !additional_discount_account}
              onCheckedChange={(checked: boolean | "indeterminate") => setAcceptAdditionalDiscount(checked === true)}
              class="mt-0.5"
            />
            <span>
              {discount_on_payment
                ? `Take ${exactCurrencyFormatter(discount_reduction_total_cents / 100)} less early-pay discount on the payment`
                : `Take ${exactCurrencyFormatter(discount_reduction_total_cents / 100)} of early-pay discount back off the credit memo`}
            </span>
          </label>
        </div>
      ) : null}

      <InvoiceBreakdownCard
        breakdown={preview.invoice_breakdown}
        accounting_system={preview.accounting_system}
        refreshing={breakdownRefreshing || breakdown_is_stale(preview.invoice_breakdown, assignments)}
        titleClass={card_title_class}
        iconClass={card_title_icon_class}
      />

      <ApplyExplainer system={preview.accounting_system} per_deduction={per_deduction_credit_memo} />
      <AccountingSyncExplainer system={preview.accounting_system} />

      {blockers.length > 0 && (
        <div class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div class="font-medium">Before you can apply</div>
          <ul class="mt-1 list-disc space-y-1 pl-5">
            {blockers.map((reason: string) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </div>
      )}

      <div class="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-4 border-t border-slate-200">
        <Link to={check_detail_path(checkNumber!)} class="sm:mr-auto">
          <Button variant="outline" class="w-full sm:w-auto">
            Cancel
          </Button>
        </Link>
        <Button
          onClick={() => void handleApply()}
          disabled={!can_apply}
          class="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto">
          {isConfirming ? (
            <span class="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              Applying…
            </span>
          ) : (
            <span class="inline-flex items-center gap-2">
              <CheckCircle className="h-4 w-4 shrink-0" />
              Apply to {preview.accounting_system}
            </span>
          )}
        </Button>
      </div>
    </div>
  )
}

interface AlreadyCreditedCardProps {
  rows: AlreadyCreditedLine[]
  /** `build_already_credited_note`, `""` when there is nothing to say. */
  note: string
  accountingSystem: string
  titleClass: string
  iconClass: string
}

interface PriceTrueUpCardProps {
  rows: PriceTrueUp[]
  customerName: string
  accountingSystem: string
  titleClass: string
  iconClass: string
}

/**
 * Remittance lines that only true the customer's figure for an invoice down to ours, folded into
 * that invoice's payment — shown so a user holding the remittance sees where each one went.
 */
function PriceTrueUpCard({ rows, customerName, accountingSystem, titleClass, iconClass }: PriceTrueUpCardProps) {
  return (
    <Card class="shadow-sm border-slate-200/80">
      <CardHeader class="space-y-1 pb-4">
        <CardTitle className={titleClass}>
          <CheckCircle className={iconClass} />
          Price corrections folded into their invoices
        </CardTitle>
        <CardDescription>
          {customerName} paid these invoices at a higher figure than the {accountingSystem} total, then took the difference back
          on this check. The invoice already carries the lower price, so each correction is part of the invoice's payment rather
          than a deduction: nothing here is credited or needs coding, and each invoice closes at its total with the early-pay
          discount shown.
        </CardDescription>
      </CardHeader>
      <CardContent class="pt-0">
        <div class="rounded-md border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow class="bg-slate-50/90 hover:bg-slate-50/90">
                <TableHead class="px-4 py-3">Remittance line</TableHead>
                <TableHead class="px-4 py-3 text-right">Amount</TableHead>
                <TableHead class="px-4 py-3">Invoice</TableHead>
                <TableHead class="px-4 py-3 text-right">Early-pay discount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row: PriceTrueUp) => (
                <TableRow key={row.invoice_line_id}>
                  <TableCell class="px-4 py-3 font-mono text-sm text-slate-900">{row.deduction_numbers.join(", ")}</TableCell>
                  <TableCell class="px-4 py-3 text-right tabular-nums">{exactCurrencyFormatter(-row.true_up_amount)}</TableCell>
                  <TableCell class="px-4 py-3 font-mono text-sm">{row.accounting_invoice_number}</TableCell>
                  <TableCell class="px-4 py-3 text-right tabular-nums">{exactCurrencyFormatter(row.discount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

interface ClosedInvoiceWashCardProps {
  rows: ClosedInvoiceWash[]
  customerName: string
  accountingSystem: string
  titleClass: string
  iconClass: string
}

/**
 * Invoices already closed in the accounting system that the customer paid and took back on this
 * check — shown so a user holding the remittance sees why both lines are missing from the tables.
 */
function ClosedInvoiceWashCard({ rows, customerName, accountingSystem, titleClass, iconClass }: ClosedInvoiceWashCardProps) {
  return (
    <Card class="shadow-sm border-slate-200/80">
      <CardHeader class="space-y-1 pb-4">
        <CardTitle className={titleClass}>
          <CheckCircle className={iconClass} />
          Closed invoices paid and taken back
        </CardTitle>
        <CardDescription>
          These invoices are already closed in {accountingSystem}. {customerName} paid each one and took the same amount back on
          this check, so the pair moves no money: neither line is applied, credited or needs coding.
        </CardDescription>
      </CardHeader>
      <CardContent class="pt-0">
        <div class="rounded-md border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow class="bg-slate-50/90 hover:bg-slate-50/90">
                <TableHead class="px-4 py-3">Invoice</TableHead>
                <TableHead class="px-4 py-3">Taken back as</TableHead>
                <TableHead class="px-4 py-3 text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row: ClosedInvoiceWash) => (
                <TableRow key={row.invoice_line_id}>
                  <TableCell class="px-4 py-3 font-mono text-sm text-slate-900">{row.accounting_invoice_number}</TableCell>
                  <TableCell class="px-4 py-3 font-mono text-sm">{row.deduction_numbers.join(", ")}</TableCell>
                  <TableCell class="px-4 py-3 text-right tabular-nums">{exactCurrencyFormatter(row.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

interface DiscountAccountPickerProps {
  accounts: LedgerAccountJson[]
  selected: LedgerAccountJson | undefined
  onSelect: (id: string) => void
}

/** The GL account an additional discount (or a discount taken back) posts to on the credit memo. */
function DiscountAccountPicker({ accounts, selected, onSelect }: DiscountAccountPickerProps) {
  return (
    <div class="flex flex-col gap-1.5 sm:max-w-md">
      <Label class="text-amber-900">Additional discount account</Label>
      <AutocompleteSelect
        values={accounts.map((a: LedgerAccountJson) => ({
          id: a.id,
          name: a.expense_account,
          account_number: a.account_number,
          expense_account: a.expense_account,
          product_service: a.product_service ?? undefined,
        }))}
        initial={selected ? { id: selected.id, name: selected.expense_account, account_number: selected.account_number } : undefined}
        onSelect={(item: SelectItem) => onSelect(item.id)}
        searchPlaceholder="Search GL accounts..."
        emptyText="No accounts found."
      />
      {!selected ? (
        <p class="text-xs text-amber-800/90">
          Pick the account the discount posts to — usually the early-pay discount account. Setting one under Settings → Accounting →
          auto-code makes it the default here.
        </p>
      ) : null}
    </div>
  )
}

/**
 * Remittance lines the accounting system has already applied as credit notes — shown above the
 * tables they are missing from, so a user holding the remittance sees where each one went instead of
 * hunting for it, and why the invoice is applied for less than its total.
 */
function AlreadyCreditedCard({ rows, note, accountingSystem, titleClass, iconClass }: AlreadyCreditedCardProps) {
  return (
    <Card class="shadow-sm border-slate-200/80">
      <CardHeader class="space-y-1 pb-4">
        <CardTitle className={titleClass}>
          <CheckCircle className={iconClass} />
          Already applied in {accountingSystem}
        </CardTitle>
        <CardDescription>
          These credits were already applied to their invoices in {accountingSystem}, and the customer took them off this
          payment. They are handled for you — nothing here needs coding, and nothing is credited twice.
        </CardDescription>
      </CardHeader>
      <CardContent class="pt-0 space-y-3">
        <div class="rounded-md border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow class="bg-slate-50/90 hover:bg-slate-50/90">
                <TableHead class="px-4 py-3">Remittance line</TableHead>
                <TableHead class="px-4 py-3 text-right">Amount</TableHead>
                <TableHead class="px-4 py-3">Applied to</TableHead>
                <TableHead class="px-4 py-3">Credit note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row: AlreadyCreditedLine) => (
                <TableRow key={row.deduction_id ?? `${row.accounting_invoice_id}:${row.credit_note_number}`} class="align-top">
                  <TableCell class="px-4 py-3 font-mono text-sm text-slate-900 whitespace-nowrap">
                    {row.deduction_id !== null && row.invoice_number !== null ? (
                      <span class="inline-flex items-center gap-1.5">
                        {row.invoice_number}
                        <AccountingDetailLink deductionId={row.deduction_id} />
                      </span>
                    ) : (
                      <span class="font-sans italic text-slate-500">Netted, not listed</span>
                    )}
                  </TableCell>
                  <TableCell class="px-4 py-3 text-right tabular-nums text-slate-800 whitespace-nowrap">
                    {exactCurrencyFormatter(-Math.abs(row.amount))}
                  </TableCell>
                  <TableCell class="px-4 py-3 font-mono text-sm text-slate-700 whitespace-nowrap">{row.accounting_invoice_number}</TableCell>
                  <TableCell class="px-4 py-3 text-sm text-slate-700">
                    <span class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium mr-2 bg-sky-100 text-sky-800">
                      Applied
                    </span>
                    <span class="font-mono">{row.credit_note_number}</span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {note ? <div class="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">{note}</div> : null}
      </CardContent>
    </Card>
  )
}

interface PrepaymentLinesCardProps {
  rows: PrepaymentSummaryRow[]
  titleClass: string
  iconClass: string
  /** The blocking message for unresolved reversals, `""` when none. */
  blocking: string
}

/**
 * UNFI `PP` lines on the remittance and what applying does with each — shown above the tables they
 * are missing from, so a user holding the remittance sees the two lines that net out (or the one
 * rewritten into an invoice payment) explained before they go looking for them.
 */
function PrepaymentLinesCard({ rows, titleClass, iconClass, blocking }: PrepaymentLinesCardProps) {
  return (
    <Card class="shadow-sm border-slate-200/80">
      <CardHeader class="space-y-1 pb-4">
        <CardTitle className={titleClass}>
          <ArrowLeftRight className={iconClass} />
          Prepayment lines
        </CardTitle>
        <CardDescription>
          UNFI "PP" lines are cash for an invoice, never spend. They are handled for you — nothing here needs coding or
          assigning.
        </CardDescription>
      </CardHeader>
      <CardContent class="pt-0 space-y-3">
        <div class="rounded-md border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow class="bg-slate-50/90 hover:bg-slate-50/90">
                <TableHead class="px-4 py-3">Remittance line</TableHead>
                <TableHead class="px-4 py-3 text-right">Amount</TableHead>
                <TableHead class="px-4 py-3">What happens</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row: PrepaymentSummaryRow) => (
                <TableRow key={row.key} class={`align-top ${row.tone === "blocked" ? "bg-red-50/60" : ""}`}>
                  <TableCell class="px-4 py-3 font-mono text-sm text-slate-900 whitespace-nowrap">
                    <div>{row.invoice_number}</div>
                    {row.paired_line ? <div class="text-slate-500">{row.paired_line.invoice_number}</div> : null}
                  </TableCell>
                  <TableCell class="px-4 py-3 text-right tabular-nums text-slate-800 whitespace-nowrap">
                    <div>{row.amount === null ? "±" : ""}{exactCurrencyFormatter(row.amount ?? 0)}</div>
                    {row.paired_line ? <div class="text-slate-500">{exactCurrencyFormatter(row.paired_line.amount)}</div> : null}
                  </TableCell>
                  <TableCell class="px-4 py-3 text-sm text-slate-700">
                    <span
                      class={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium mr-2 ${
                        row.tone === "blocked" ? "bg-red-100 text-red-700" : "bg-sky-100 text-sky-800"
                      }`}
                    >
                      {row.label}
                    </span>
                    {row.note}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {blocking ? <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{blocking}</div> : null}
      </CardContent>
    </Card>
  )
}

/** One of the two either/or outcomes for a short pay; ticking it is also the go-ahead to apply. */
function ShortPayChoice({
  checked,
  onCheckedChange,
  title,
  when,
  effect,
}: {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  title: string
  when: string
  effect: string
}) {
  return (
    <label
      class={`flex items-start gap-2 cursor-pointer rounded-md border px-3 py-2 ${checked ? "border-amber-400 bg-amber-100/60" : "border-amber-200 bg-white/50"}`}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={(next: boolean | "indeterminate") => onCheckedChange(next === true)}
        class="mt-0.5"
      />
      <span class="space-y-0.5">
        <span class="block font-medium">{title}</span>
        <span class="block">{when}</span>
        <span class="block text-amber-800/80">{effect}</span>
      </span>
    </label>
  )
}
